import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Star, Check, Loader2, MessageCircle } from "lucide-react";
import { api, openWhatsApp } from "@/lib/api";
import { ProductImage } from "@/components/ProductImage";

const TABS = [
  { id: "benefits", label: "Benefits" },
  { id: "dosage", label: "How to Use" },
  { id: "facts", label: "Nutrition" },
];

export default function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [tab, setTab] = useState("benefits");

  useEffect(() => {
    api.get("/products").then((r) => setProduct(r.data.find((p) => p.id === id))).catch(() => {});
  }, [id]);

  if (!product) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
      </div>
    );
  }

  const order = () => {
    const msg = `Hello U&URS NUTRITIONIST 👋\n\nI'd like to order:\n*${product.name}* (${product.weight})\nPrice: ₹${product.price.toLocaleString("en-IN")}\n\nPlease share payment & delivery details.`;
    openWhatsApp(msg);
  };

  return (
    <div className="relative h-full animate-fade-in">
      {/* Image banner */}
      <div className="relative h-72 bg-slate-100 dark:bg-emerald-800">
        <ProductImage product={product} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        <button
          data-testid="product-back-btn"
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 dark:bg-emerald-950/90 backdrop-blur flex items-center justify-center text-slate-700 dark:text-slate-100 shadow"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        {product.tag && (
          <span className="absolute top-5 right-4 text-[11px] font-bold uppercase tracking-wide bg-emerald-700 text-white px-3 py-1 rounded-full">{product.tag}</span>
        )}
      </div>

      <div className="px-5 pt-5 pb-32 -mt-5 relative bg-stone-50 dark:bg-emerald-950 rounded-t-3xl">
        <span className="text-[11px] font-semibold tracking-wider uppercase text-emerald-600 dark:text-emerald-400">{product.category}</span>
        <h1 className="mt-1 text-xl font-extrabold text-emerald-950 dark:text-emerald-50 font-head leading-snug">{product.name}</h1>

        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center gap-1 text-sm">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            <span className="font-bold text-emerald-950 dark:text-emerald-50">{product.rating}</span>
            <span className="text-slate-400 text-xs">({product.reviews} reviews)</span>
          </div>
          <span className="text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-emerald-900 px-2.5 py-1 rounded-full">{product.weight}</span>
        </div>

        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-2xl font-extrabold text-emerald-700 dark:text-emerald-400">₹{product.price.toLocaleString("en-IN")}</span>
          <span className="text-sm text-slate-400 line-through">₹{product.original_price.toLocaleString("en-IN")}</span>
        </div>

        {/* Tabs */}
        <div className="mt-5 grid grid-cols-3 gap-1 p-1 bg-slate-100 dark:bg-emerald-900/50 rounded-2xl">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`product-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`py-2.5 rounded-xl text-xs font-semibold transition-all ${
                tab === t.id ? "bg-white dark:bg-emerald-700 text-emerald-800 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-400"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-4" data-testid="product-tab-content">
          {tab === "benefits" && (
            <>
              {product.description && <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed mb-3" data-testid="product-description">{product.description}</p>}
              <ul className="space-y-2.5">
              {product.benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-800 flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-emerald-700 dark:text-emerald-300" />
                  </span>
                  <span className="text-sm text-slate-700 dark:text-slate-300">{b}</span>
                </li>
              ))}
            </ul>
            </>
          )}
          {tab === "dosage" && (
            <div className="space-y-3">
              <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed bg-white dark:bg-emerald-900/50 p-4 rounded-2xl border border-slate-100 dark:border-emerald-800">
                {product.dosage}
              </p>
              {product.ingredients?.length > 0 && (
                <div className="bg-white dark:bg-emerald-900/50 p-4 rounded-2xl border border-slate-100 dark:border-emerald-800" data-testid="product-ingredients">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">Ingredients</p>
                  <p className="text-sm text-slate-700 dark:text-slate-300">{product.ingredients.join(", ")}</p>
                </div>
              )}
              {product.precautions?.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-500/10 p-4 rounded-2xl border border-amber-100 dark:border-amber-500/20" data-testid="product-precautions">
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300 mb-1.5">Precautions</p>
                  <ul className="space-y-1">{product.precautions.map((p, i) => <li key={i} className="text-sm text-amber-900 dark:text-amber-100">• {p}</li>)}</ul>
                </div>
              )}
            </div>
          )}
          {tab === "facts" && (
            <div className="rounded-2xl overflow-hidden border border-slate-100 dark:border-emerald-800">
              {Object.entries(product.nutritional_facts).map(([k, v], i) => (
                <div key={k} className={`flex items-center justify-between px-4 py-2.5 text-sm ${i % 2 === 0 ? "bg-white dark:bg-emerald-900/50" : "bg-slate-50 dark:bg-emerald-900/20"}`}>
                  <span className="text-slate-600 dark:text-slate-400">{k}</span>
                  <span className="font-semibold text-emerald-950 dark:text-emerald-50">{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Sticky order bar */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-white/95 dark:bg-emerald-950/95 backdrop-blur-xl border-t border-slate-200 dark:border-emerald-800">
        <button
          data-testid="product-order-whatsapp-btn"
          onClick={order}
          className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 rounded-2xl transition-colors shadow-lg shadow-emerald-600/20"
        >
          <MessageCircle className="w-5 h-5" /> Order via WhatsApp
        </button>
      </div>
    </div>
  );
}
